import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ModelSelection from './ModelSelection';
import ClothingSelection from './ClothingSelection';
import PoseSelection from './PoseSelection';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { tryonExamplePresets, ExamplePreset } from './examplePresets';
import { getSubscriptionStatus, SubscriptionType, isProPlan, isEnterprisePlan } from '../../services/subscriptionService';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import CustomSelect from '../ui/CustomSelect';

interface TryOnSettings {
  style: string;
  variations: number;
  prompt: string;
  suffixPrompt: string;
  clothType: string;
  customClothType: string;
}

interface LocationState {
  inputImages: string[];
  prompt: string;
  style: string;
  variations: number;
  recreatingFrom: string;
  generatedImages?: string[];
}

interface FashionModelItem {
  id: string;
  imageUrl: string[];
}

interface ClothingItem {
  id: string;
  imageUrl: string[];
}

interface PoseItem {
  poseId: string;
  imageUrl: string[];
  prompt: string;
  type: string;
  gender: string;
  generationType: string;
  timestamp: string;
  liveStatus?: boolean;
  size?: string;
}

interface TryOnApiPayload {
  sceneStyle: string;
  colorTheme: string;
  clothing: ClothingItem;
  fashionModel: FashionModelItem | null;
  pose?: PoseItem | null;
  imageSize: string;
  prompt: string;
  numImages: number;
  numInferenceSteps: number;
  guidanceScale: number;
  outputFormat: string;
  enableSafetyChecker: boolean;
  userId: string;
  mode: string;
}

// Fashion industry popular background colors
const BACKGROUND_OPTIONS = [
  { id: 'white', name: 'White', color: '#ffffff', description: 'Clean white background', value: 'white' },
  { id: 'black', name: 'Black', color: '#000000', description: 'Solid black background', value: 'black' },
  { id: 'cream', name: 'Cream', color: '#f5f5dc', description: 'Pastel cream background', value: 'pastel cream' },
  { id: 'red', name: 'Red', color: '#ffb3ba', description: 'Pastel red background', value: 'pastel red' },
  { id: 'yellow', name: 'Yellow', color: '#ffdfba', description: 'Pastel yellow background', value: 'pastel yellow' },
  { id: 'brown', name: 'Brown', color: '#d4a574', description: 'Pastel brown background', value: 'pastel brown' },
  { id: 'grey', name: 'Grey', color: '#d3d3d3', description: 'Pastel grey background', value: 'pastel grey' }
];

// Cloth type options
const CLOTH_TYPE_OPTIONS = [
  { value: 'auto', label: 'Automatic' },
  { value: 'custom', label: 'Custom' },
  { value: 'dress', label: 'Dress' },
  { value: 'jeans', label: 'Jeans' },
  { value: 'kurti', label: 'Kurti' },
  { value: 'kurta', label: 'Kurta' },
  { value: 'pants', label: 'Pants' },
  { value: 'shirt', label: 'Shirt' },
  { value: 'saree', label: 'Saree' },
  { value: 'shorts', label: 'Shorts' },
  { value: 'suit', label: 'Suit' },
  { value: 'tshirt', label: 'T-Shirt' },
  { value: 'top', label: 'Top' },
  { value: 'underwear', label: 'Underwear' }
];

const TryOn: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [selectedModelId, setSelectedModelId] = useState<string | null>('random');
  const [clothingImageUrls, setClothingImageUrls] = useState<string[]>([]);
  const [selectedPoseId, setSelectedPoseId] = useState<string | null>('none');
  const [genderFilter, setGenderFilter] = useState<string>('female');
  const [sizeFilter, setSizeFilter] = useState<string>('normal');
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>(state?.generatedImages || []);
  const [generationTime, setGenerationTime] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  // Settings state
  const [settings, setSettings] = useState<TryOnSettings>({
    style: state?.style || 'simple-background',
    variations: state?.variations || 2,
    prompt: state?.prompt || '',
    suffixPrompt: '',
    clothType: 'auto',
    customClothType: ''
  });

  const [mode, setMode] = useState<string>('performance'); // Start with performance (always available)
  const [selectedBackground, setSelectedBackground] = useState<string>('yellow');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isExample, setIsExample] = useState(false);
  const [selectedClothingExample, setSelectedClothingExample] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<string>('9:16');
  const [selectedView, setSelectedView] = useState<string>('random');
  const [hasVideoAccess, setHasVideoAccess] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadedClothingUrl, setUploadedClothingUrl] = useState<string | null>(null);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('tryon');

  // Mode restrictions hook
  const modeRestrictions = useModeRestrictions();

  // Effect to handle recreation state
  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length >= 1) {
        try {
          // Load clothing image (first priority)
          setClothingImageUrls([state.inputImages[0]]);

          // If there's a second image, we could potentially set a model, but since we're using pre-made models now,
          // we'll skip this logic

          // Set generated images if they exist
          if (state.generatedImages) {
            setGeneratedImages(state.generatedImages);
          }
        } catch (error) {
          console.error('Failed to load input images:', error);
        }
      }
    };

    loadInputImages();
  }, [state]);

  // Clear error messages when user uploads new images or selects model
  useEffect(() => {
    if (clothingImageUrls.length > 0 || selectedModelId) {
      setErrorMessage('');
    }
  }, [clothingImageUrls, selectedModelId]);

  // Disable model and pose selection when user writes a prompt
  useEffect(() => {
    if (settings.prompt && settings.prompt.trim().length > 0) {
      setSelectedModelId('random');
      setSelectedPoseId(null);
    }
  }, [settings.prompt]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Check subscription status for video generation access
  useEffect(() => {
    const checkVideoAccess = async () => {
      if (!currentUser?.uid) {
        setHasVideoAccess(false);
        return;
      }

      try {
        const response = await getSubscriptionStatus(currentUser.uid);
        const hasAccess = isProPlan(response.type) || isEnterprisePlan(response.type);
        setHasVideoAccess(hasAccess);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setHasVideoAccess(false);
      }
    };

    checkVideoAccess();
  }, [currentUser]);

  // Handle mode restrictions - automatically switch to available mode if current is restricted
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
      // Switch to the first available mode (Performance is always available)
      const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
      if (availableMode) {
        setMode(availableMode.value);
      }
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode]);

  // Credit estimation effect - trigger when generation settings change
  useEffect(() => {
    // Only estimate if we have required data and user is authenticated
    if (!currentUser?.uid || clothingImageUrls.length === 0) {
      return;
    }

    const estimateCost = async () => {
      // Build the same payload structure as the actual generation
      const effectiveClothType = settings.clothType === 'custom' ? settings.customClothType : settings.clothType;

      // Prepare pose data for credit estimation
      let poseData = null;
      if (selectedPoseId) {
        if (selectedPoseId === 'none') {
          // Random pose case
          poseData = {
            poseId: "-1",
            imageUrl: [""],
            prompt: "",
            type: "",
            gender: "",
            generationType: "pose",
            liveStatus: true,
            timestamp: new Date().toISOString()
          };
        } else {
          try {
            // Fetch pose data from the API for specific pose
            const poseResponse = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);
            const poseApiData = await poseResponse.json();

            if (poseApiData.success && poseApiData.poses) {
              const selectedPoseData = poseApiData.poses.find((pose: any) => pose.poseId === selectedPoseId);
              if (selectedPoseData) {
                const imageUrl = Array.isArray(selectedPoseData.imageUrl) ? selectedPoseData.imageUrl[0] : selectedPoseData.imageUrl;
                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

                poseData = {
                  poseId: selectedPoseData.poseId,
                  imageUrl: [cleanImageUrl],
                  prompt: selectedPoseData.prompt || "",
                  type: selectedPoseData.type || "",
                  gender: selectedPoseData.gender || "",
                  generationType: selectedPoseData.generationType || "pose",
                  liveStatus: selectedPoseData.liveStatus,
                  timestamp: selectedPoseData.timestamp || new Date().toISOString(),
                  size: selectedPoseData.size || "normal"
                };
              }
            }
          } catch (error) {
            console.error('Failed to fetch pose data for credit estimation:', error);
            // Use fallback pose data if fetch fails
            poseData = {
              poseId: selectedPoseId,
              imageUrl: [""],
              prompt: "",
              type: "",
              gender: "",
              generationType: "pose",
              liveStatus: true,
              timestamp: new Date().toISOString()
            };
          }
        }
      }

      const estimationPayload = {
        sceneStyle: settings.style.toLowerCase(),
        colorTheme: BACKGROUND_OPTIONS.find(bg => bg.id === selectedBackground)?.value || 'white',
        clothing: {
          id: "clothing_estimation",
          imageUrl: clothingImageUrls,
          type: effectiveClothType
        },
        fashionModel: selectedModelId === 'random' ? {
          modelId: "-1",
          imageUrl: [""],
          prompt: "",
          type: "",
          timestamp: new Date().toISOString(),
          gender: "",
          generationType: "try-on",
          seed: 0,
          modelName: "Random Model",
          region: "",
          method: "random"
        } : null,
        pose: poseData,
        method: selectedModelId === 'random' ? "random" : "preselected",
        aspectRatio: aspectRatio,
        viewType: selectedView,
        imageSize: "square",
        prompt: settings.prompt || '',
        suffixPrompt: settings.suffixPrompt || '',
        numImages: settings.variations,
        numInferenceSteps: 28,
        guidanceScale: 3.5,
        outputFormat: "jpeg",
        enableSafetyChecker: true,
        mode: mode.toLowerCase(),
        quality: "high",
        clothType: effectiveClothType
      };

      estimateCreditCost(estimationPayload);
    };

    estimateCost();
  }, [
    currentUser?.uid,
    clothingImageUrls,
    settings.variations,
    settings.clothType,
    settings.customClothType,
    mode,
    selectedPoseId,
    estimateCreditCost
  ]);

  const startTimer = () => {
    setGenerationTime(0);
    const interval = setInterval(() => {
      setGenerationTime(prev => prev + 0.1);
    }, 100);
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const handleTryOn = async () => {
    if (clothingImageUrls.length === 0) {
      alert('Please upload a clothing image');
      return;
    }

    // Check if user can afford the generation
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsProcessing(true);
    setGeneratedImages([]);
    setIsExample(false);
    setErrorMessage('');
    startTimer(); // Start the timer

    try {
      // Determine the method based on model selection
      let method = "random"; // Default for future custom uploads
      if (selectedModelId === 'random') {
        method = "random";
      } else if (selectedModelId && selectedModelId !== 'random') {
        method = "preselected";
      }

      // Prepare model data based on selection
      let modelData = null;
      if (selectedModelId) {
        if (selectedModelId === 'random') {
          // Random model case
          modelData = {
            modelId: "-1",
            imageUrl: [""],
            prompt: "",
            type: "",
            timestamp: new Date().toISOString(),
            gender: "",
            generationType: "try-on",
            seed: 0,
            modelName: "Random Model",
            region: "",
            method: method
          };
        } else {
          // Specific model selected - find the model from the API response
          const response = await fetch(API_CONFIG.GET_TRYON_MODELS_URL);
          const data = await response.json();

          if (data.success && data.models) {
            const selectedModel = data.models.find((model: any) => model.modelId === selectedModelId);
            if (selectedModel) {
              const imageUrl = Array.isArray(selectedModel.imageUrl) ? selectedModel.imageUrl[0] : selectedModel.imageUrl;
              const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

              modelData = {
                modelId: selectedModel.modelId,
                imageUrl: [cleanImageUrl],
                prompt: selectedModel.prompt || "",
                type: selectedModel.type || "",
                timestamp: selectedModel.timestamp || new Date().toISOString(),
                gender: selectedModel.gender || "",
                generationType: selectedModel.generationType || "try-on",
                seed: selectedModel.seed || 0,
                modelName: selectedModel.modelName || "",
                region: selectedModel.region || "",
                method: method
              };
            }
          }
        }
      }

      // Prepare pose data based on selection
      let poseData = null;
      if (selectedPoseId) {
        if (selectedPoseId === 'none') {
          // Random pose case
          poseData = {
            poseId: "-1",
            imageUrl: [""],
            prompt: "",
            type: "",
            gender: "",
            generationType: "pose",
            liveStatus: true,
            timestamp: new Date().toISOString()
          };
        } else {
          try {
            // Fetch pose data from the API for specific pose
            const poseResponse = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);
            const poseApiData = await poseResponse.json();

            if (poseApiData.success && poseApiData.poses) {
              const selectedPoseData = poseApiData.poses.find((pose: any) => pose.poseId === selectedPoseId);
              if (selectedPoseData) {
                const imageUrl = Array.isArray(selectedPoseData.imageUrl) ? selectedPoseData.imageUrl[0] : selectedPoseData.imageUrl;
                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

                poseData = {
                  poseId: selectedPoseData.poseId,
                  imageUrl: [cleanImageUrl],
                  prompt: selectedPoseData.prompt || "",
                  type: selectedPoseData.type || "",
                  gender: selectedPoseData.gender || "",
                  generationType: selectedPoseData.generationType || "pose",
                  liveStatus: selectedPoseData.liveStatus,
                  timestamp: selectedPoseData.timestamp || new Date().toISOString(),
                  size: selectedPoseData.size || "normal"
                };
              }
            }
          } catch (error) {
            console.error('Failed to fetch pose data:', error);
            // Continue without pose data if fetch fails
          }
        }
      }

      const effectiveClothType = settings.clothType === 'custom' ? settings.customClothType : settings.clothType;

      const payload = {
        sceneStyle: settings.style.toLowerCase(),
        colorTheme: BACKGROUND_OPTIONS.find(bg => bg.id === selectedBackground)?.value || 'white',
        clothing: {
          id: "clothing_" + Date.now(),
          imageUrl: clothingImageUrls,
          type: effectiveClothType
        },
        fashionModel: modelData,
        pose: poseData,
        method: method,
        aspectRatio: aspectRatio,
        viewType: selectedView,
        imageSize: "square",
        prompt: settings.prompt || '',
        suffixPrompt: settings.suffixPrompt || '',
        numImages: settings.variations,
        numInferenceSteps: 28,
        guidanceScale: 3.5,
        outputFormat: "jpeg",
        enableSafetyChecker: true,
        userId: currentUser?.uid || "user_id",
        mode: mode.toLowerCase(),
        quality: "high",
        clothType: effectiveClothType
      };

      const response = await fetch(API_CONFIG.VIRTUAL_TRYON_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Check HTTP status
      if (!response.ok) {
        setErrorMessage('Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      // Check if images were returned
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
        // Refresh user credits after successful generation
        await refreshUserCredits();
        setErrorMessage('');

        // Auto scroll to results after images are rendered
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate try-on:', error);
      setErrorMessage('Failed to generate try-on. Please try again.');
    } finally {
      stopTimer(); // Stop the timer
      setIsProcessing(false);
    }
  };

  // Example handling for clothing
  const handleClothingExampleSelect = (clothingUrl: string, clothingId: number) => {
    setSelectedClothingExample(clothingId);
    setIsExample(true);

    // Find matching preset based on clothing URL
    const matchingPreset = tryonExamplePresets.find(preset => preset.clothPreview === clothingUrl);
    if (matchingPreset) {
      setSelectedPreset(matchingPreset.id);
      setSettings({ ...settings, prompt: matchingPreset.prompt });
      setGeneratedImages(matchingPreset.results);
    }
  };

  const handleModelSelect = (modelUrl: string, modelId: string) => {
    // Model selection handled by the ModelSelection component
    console.log('Model selected:', modelId, modelUrl);
  };

  const handlePoseSelect = (poseUrl: string, poseId: string) => {
    // Pose selection handled by the PoseSelection component
    console.log('Pose selected:', poseId, poseUrl);
  };

  const resetImages = () => {
    setSelectedModelId('random');
    setClothingImageUrls([]);
    setGeneratedImages([]);
    setSelectedPreset(null);
    setIsExample(false);
    setSelectedClothingExample(null);
    setSelectedPoseId('none');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3">
      {/* Responsive Grid Layout - 4 columns on laptop screens and larger */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Column 1: Image Upload */}
        <div className="lg:col-span-1 h-[730px]">
          <ClothingSelection
            imageUrls={clothingImageUrls}
            setImageUrls={setClothingImageUrls}
            isExample={isExample}
            onExampleSelect={handleClothingExampleSelect}
          />
        </div>

        {/* Column 2: Model Selector */}
        <div className="lg:col-span-1 h-[730px]">
          <ModelSelection
            selectedModelId={selectedModelId}
            setSelectedModelId={setSelectedModelId}
            onModelSelect={handleModelSelect}
            disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
            genderFilter={genderFilter}
            onGenderFilterChange={setGenderFilter}
          />
        </div>

        {/* Column 3: Pose Selector */}
        <div className="lg:col-span-1 h-[730px]">
          <PoseSelection
            selectedPoseId={selectedPoseId}
            setSelectedPoseId={setSelectedPoseId}
            onPoseSelect={handlePoseSelect}
            disabled={Boolean(settings.prompt && settings.prompt.trim().length > 0)}
            genderFilter={genderFilter}
            sizeFilter={sizeFilter}
            onSizeFilterChange={setSizeFilter}
          />
        </div>

        {/* Column 4: Dropdown Menus */}
        <div className="lg:col-span-1 2xl:col-span-1 min-w-0 h-[730px]">
          {/* Settings Panel */}
          <div className="bg-accent-sand rounded-lg p-4 space-y-3 min-w-0 w-full h-full pr-2">
            <div className="space-y-3">
              {/* Try-On Style */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Choose Background</label>
                <CustomSelect
                  value={settings.style}
                  onChange={(value) => setSettings({ ...settings, style: value })}
                  options={[
                    { value: 'natural', label: 'Natural' },
                    { value: 'studio', label: 'Studio' },
                    { value: 'outdoor', label: 'Outdoor' },
                    { value: 'minimalist', label: 'Minimalist' },
                    { value: 'simple-background', label: 'Simple Background' }
                  ]}
                  className="w-full"
                />
              </div>



              {/* Background Color Selector - Appears right after Try-On Style */}
              {settings.style === 'simple-background' && (
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-text-heading mb-2 whitespace-nowrap">Background</label>
                  <div className="grid grid-cols-7 gap-2 min-w-0">
                    {BACKGROUND_OPTIONS.map((bg) => (
                      <button
                        key={bg.id}
                        onClick={() => setSelectedBackground(bg.id)}
                        className={`relative h-12 rounded-lg border-2 transition-all duration-200 group ${selectedBackground === bg.id
                          ? 'border-secondary-500 ring-2 ring-secondary-200'
                          : 'border-accent-taupe hover:border-accent-taupe'
                          }`}
                        style={{ backgroundColor: bg.color }}
                        title={`${bg.name} - ${bg.description}`}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-xs font-medium px-1 py-0.5 rounded ${bg.id === 'black'
                            ? 'text-white bg-black bg-opacity-20'
                            : 'text-text-heading bg-white bg-opacity-70'
                            }`}>
                            {bg.name}
                          </span>
                        </div>
                        {selectedBackground === bg.id && (
                          <div className="absolute top-1 right-1">
                            <svg className="w-3 h-3 text-secondary-600 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-description mt-1">
                    {BACKGROUND_OPTIONS.find(bg => bg.id === selectedBackground)?.description}
                  </p>
                </div>
              )}



              {/* Variations */}
              <div className="min-w-0">
                <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Number of Images</label>
                <div className="flex items-center justify-between border border-accent-taupe rounded-lg h-[42px] bg-white min-w-0">
                  <button
                    onClick={() => setSettings({ ...settings, variations: Math.max(1, settings.variations - 1) })}
                    disabled={settings.variations <= 1}
                    className={`flex-1 h-full flex items-center justify-center rounded-l-lg ${settings.variations <= 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-text-heading hover:bg-accent-sand transition-colors'
                      }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M3.75 12a.75.75 0 01.75-.75h15a.75.75 0 010 1.5h-15a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <div className="flex-1 text-center font-medium text-text-heading select-none">
                    {settings.variations}
                  </div>

                  <button
                    onClick={() => setSettings({ ...settings, variations: Math.min(4, settings.variations + 1) })}
                    disabled={settings.variations >= 4}
                    className={`flex-1 h-full flex items-center justify-center rounded-r-lg ${settings.variations >= 4
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-text-heading hover:bg-accent-sand transition-colors'
                      }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v7.5h7.5a.75.75 0 010 1.5h-7.5v7.5a.75.75 0 01-1.5 0v-7.5h-7.5a.75.75 0 010-1.5h7.5v-7.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="border-t border-accent-taupe pt-3">
              <button
                type="button"
                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-text-heading hover:text-text-heading transition-colors"
              >
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Advanced Settings
                </span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${showAdvancedSettings ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAdvancedSettings && (
                <div className="mt-3 space-y-3">
                  {/* Cloth Type */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Cloth Type</label>
                    <CustomSelect
                      value={settings.clothType}
                      onChange={(value) => setSettings({ ...settings, clothType: value })}
                      options={CLOTH_TYPE_OPTIONS.map((option) => ({
                        value: option.value,
                        label: option.label
                      }))}
                      className="w-full"
                    />
                    {settings.clothType === 'custom' && (
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder="Enter custom cloth type (e.g., blazer, cardigan, hoodie)"
                          className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 text-sm"
                          value={settings.customClothType}
                          onChange={(e) => setSettings({ ...settings, customClothType: e.target.value })}
                        />
                      </div>
                    )}
                  </div>

                  {/* Mode */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">
                      Mode
                      {!modeRestrictions.hasInitialLoad && (
                        <span className="ml-2 text-xs text-text-description">(Loading...)</span>
                      )}
                    </label>
                    <div className="relative">
                      <CustomSelect
                        value={mode}
                        onChange={(value) => setMode(value)}
                        options={modeRestrictions.availableModes.map((modeOption) => {
                          let planRequirement = '';
                          if (modeOption.disabled && modeOption.requiresPlan) {
                            const minPlan = modeOption.requiresPlan[0];
                            switch (minPlan) {
                              case 'plus':
                                planRequirement = ' (Plus Plan Required)';
                                break;
                              case 'pro':
                                planRequirement = ' (Pro Plan Required)';
                                break;
                              case 'enterprise':
                                planRequirement = ' (Enterprise Plan Required)';
                                break;
                              default:
                                planRequirement = ' (Upgrade Required)';
                            }
                          }
                          return {
                            value: modeOption.value,
                            label: `${modeOption.label}${planRequirement}`,
                            disabled: modeOption.disabled
                          };
                        })}
                        disabled={!modeRestrictions.hasInitialLoad}
                        className="w-full"
                      />
                      {modeRestrictions.error && (
                        <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
                      )}
                      {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
                        <p className="text-xs text-orange-600 mt-1">
                          <i className="fas fa-exclamation-triangle mr-1"></i>
                          {modeRestrictions.getRestrictedModeMessage(mode)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* View */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">View</label>
                    <CustomSelect
                      value={selectedView}
                      onChange={(value) => setSelectedView(value)}
                      options={[
                        { value: 'random', label: 'Random' },
                        { value: 'full body view', label: 'Full-Body' },
                        { value: 'upper body view', label: 'Upper-Body' },
                        { value: 'closeup view', label: 'Closeup' },
                        { value: 'side view', label: 'Side View' },
                        { value: 'back view', label: 'Back View' }
                      ]}
                      className="w-full"
                    />
                  </div>

                  {/* Aspect Ratio */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Aspect Ratio</label>
                    <CustomSelect
                      value={aspectRatio}
                      onChange={(value) => setAspectRatio(value)}
                      options={[
                        { value: '9:16', label: 'Portrait (9:16)' },
                        { value: '16:9', label: 'Landscape (16:9)' },
                        { value: '1:1', label: 'Square (1:1)' }
                      ]}
                      className="w-full"
                    />
                  </div>

                  {/* Model Size */}
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Model Size</label>
                    <CustomSelect
                      value={sizeFilter}
                      onChange={(value) => setSizeFilter(value)}
                      options={[
                        { value: 'normal', label: 'Normal' },
                        { value: 'plus', label: 'Plus' }
                      ]}
                      className="w-full"
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Prompt</label>
                    <textarea
                      value={settings.prompt}
                      onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                      placeholder="Add any specific instructions for the try-on (e.g., brazilian woman wearing this dress in a sunlit tropical garden, radiating confidence and effortless elegance)"
                      className="w-full border border-accent-taupe rounded-lg px-3 py-2 focus:ring-secondary-500 focus:border-secondary-500 min-h-[80px] resize-y text-sm min-w-0"
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-text-heading mb-1 whitespace-nowrap">Suffix Prompt</label>
                    <textarea
                      value={settings.suffixPrompt}
                      onChange={(e) => setSettings({ ...settings, suffixPrompt: e.target.value })}
                      placeholder="Add suffix to append to your prompt (e.g., high quality, detailed, 8k)"
                      className="w-full border border-accent-taupe rounded-lg px-3 py-2 focus:ring-secondary-500 focus:border-secondary-500 min-h-[60px] resize-y text-sm min-w-0"
                    />
                    <p className="text-xs text-text-description mt-1">Appended to the prompt. Does not affect model or pose selection.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Generate button - Below Advanced Settings */}
            <div className="border-t border-accent-taupe pt-3 mt-3">
              {/* Clear Button */}
              <button
                onClick={() => {
                  // Reset model selection
                  setSelectedModelId('random');
                  // Clear clothing images
                  setClothingImageUrls([]);
                  // Reset pose selection
                  setSelectedPoseId('none');
                  // Reset all settings to defaults
                  setSettings({
                    style: 'simple-background',
                    variations: 2,
                    prompt: '',
                    suffixPrompt: '',
                    clothType: 'auto',
                    customClothType: ''
                  });
                  // Reset other selections
                  setSelectedBackground('white');
                  setSelectedView('random');
                  setAspectRatio('9:16');
                  setMode('performance');
                  // Clear generated images and examples
                  setGeneratedImages([]);
                  setIsExample(false);
                  setSelectedPreset(null);
                  setSelectedClothingExample(null);
                  setErrorMessage('');
                  // Reset advanced settings
                  setShowAdvancedSettings(false);
                }}
                className="w-full py-3 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 mb-3"
                title="Clear all settings, images, and results"
              >
                Clear All Settings
              </button>

              <button
                className={`w-full py-3 rounded-lg font-medium transition-colors ${clothingImageUrls.length > 0
                  ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                  : 'bg-gray-300 text-text-description cursor-not-allowed'
                  }`}
                onClick={handleTryOn}
                disabled={clothingImageUrls.length === 0 || isProcessing}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center space-x-3">
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Generating...</span>
                    <div className="flex items-center space-x-1 opacity-90">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-mono font-semibold text-sm">
                        {generationTime.toFixed(1)}s
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <i className="fas fa-tshirt mr-2"></i>
                    Generate Try-On
                  </>
                )}
              </button>

              {/* Credit Cost Preview - Below Generate Button */}
              <div className="mt-3">
                <CreditCostPreview
                  creditCost={creditEstimation.creditCost}
                  isEstimating={creditEstimation.isEstimating}
                  error={creditEstimation.error}
                  canAfford={creditEstimation.canAfford}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Generated Results Section - Below both columns */}
      {generatedImages.length > 0 && (
        <div ref={resultsRef} className="mt-6">
          <h3 className="text-lg font-semibold mb-3">
            {isExample ? 'Example Results' : 'Generated Results'}
            {isExample && (
              <span className="text-sm font-normal text-text-description ml-2">
                (Upload your own images and click Generate to create your own)
              </span>
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {generatedImages.map((url, idx) => (
              <div
                key={idx}
                className="relative flex flex-col h-80 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 group overflow-hidden border border-accent-taupe"
              >
                {/* Overlay index badge */}
                <div className="absolute top-2 left-2 z-10 bg-secondary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow">
                  #{idx + 1}
                </div>
                {/* Image area */}
                <div className="flex-1 flex items-center justify-center bg-accent-sand">
                  <img
                    src={url}
                    alt={`${isExample ? 'Example' : 'Generated'} ${idx + 1}`}
                    className="max-h-60 max-w-full object-contain drop-shadow rounded-xl transition-transform duration-300 group-hover:scale-105 bg-white"
                  />
                </div>
                {/* Action bar */}
                <div className="flex justify-center items-center gap-2 px-4 py-2 bg-white rounded-b-2xl shadow-md mt-2">
                  {/* Download Button */}
                  <button
                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                    onClick={async () => {
                      try {
                        const response = await fetch(url);
                        const blob = await response.blob();
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = `${isExample ? 'example' : 'try-on'}-result-${idx + 1}.jpg`;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        // Cleanup blob URL to free memory
                        window.URL.revokeObjectURL(blobUrl);
                      } catch (error) {
                        console.error('Download failed:', error);
                        // Fallback to direct download if blob method fails
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${isExample ? 'example' : 'try-on'}-result-${idx + 1}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    Download
                  </button>
                  {/* Generate Video Button */}
                  <button
                    className={`relative px-3 py-1.5 rounded-lg transition-colors text-sm font-medium border ${hasVideoAccess
                      ? 'text-text-description hover:text-secondary-600 hover:bg-secondary-100 border-accent-taupe hover:border-secondary-300'
                      : 'text-text-description cursor-not-allowed bg-accent-sand border-accent-taupe'
                      }`}
                    onClick={() => {
                      if (hasVideoAccess) {
                        navigate('/canvas-editor', {
                          state: {
                            inputImages: [url],
                            prompt: '',
                            style: '',
                            variations: 1,
                            recreatingFrom: '',
                          }
                        });
                      } else {
                        navigate('/subscription');
                      }
                    }}
                  >
                    Create Video
                    {!hasVideoAccess && (
                      <div className="status-dot-top-right">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                  {/* Upscale Button */}
                  <button
                    className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                    onClick={() => {
                      navigate('/canvas-editor', {
                        state: {
                          inputImages: [url],
                          prompt: '',
                          style: '',
                          variations: 1,
                          recreatingFrom: '',
                          tool: 'upscaler'
                        }
                      });
                    }}
                  >
                    HD
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Try-On Generation"
      />
    </div>
  );
};

export default TryOn; 